import axios, { isAxiosError } from "axios";
import LoadingSpinner from "components/LoadingSpinner";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "services/authService";
import TokenService from "services/tokenService";
import { getURIVariable } from "utils/socialLoginUtils";
import { StorageUtils } from "utils/storageUtils";
import { useLazyMyInfoQuery, useRegisterMutation } from "app/apiSlice";

interface UserInfo {
  data: {
    id: number;
    kakao_account: {
      email: string;
      name?: string;
      phone_number?: string;
    };
  };
}

const normalizeKakaoPhone = (num = "") =>
  num.replace(/^\+82/, "0").replace(/[-\s]/g, "");

const KaKaoCallback = () => {
  const navigate = useNavigate();
  const [fetchMyInfo] = useLazyMyInfoQuery();
  const [register] = useRegisterMutation();

  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get("code");
  const isModal = StorageUtils.IsLoginModal.get();

  const userInfoRef = useRef<UserInfo>();

  useEffect(() => {
    if (!code) {
      navigate("/login");
      return;
    }

    const [KAKAO_CLIENT_ID, KAKAO_REDIRECT_URI] = getURIVariable("KAKAO");
    const KAKAO_TOKEN_URI = `https://kauth.kakao.com/oauth/token?grant_type=authorization_code&client_id=${KAKAO_CLIENT_ID}&redirect_uri=${KAKAO_REDIRECT_URI}`;
    const KAKAO_USER_URI = "https://kapi.kakao.com/v2/user/me";

    const getToken = async () => {
      try {
        const tokenResponse = await axios.post(
          `${KAKAO_TOKEN_URI}&code=${code}`
        );

        const userResponse = await axios.get(KAKAO_USER_URI, {
          headers: {
            Authorization: `Bearer ${tokenResponse.data.access_token}`,
          },
        });

        userInfoRef.current = userResponse;
      } catch (error) {
        if (isAxiosError(error)) {
          console.error(error);
        }
        alert("Kakao signin failed");
        navigate("/login");
        return;
      }

      const rawPhone = userInfoRef.current?.data.kakao_account.phone_number;
      const normalizedPhone = normalizeKakaoPhone(rawPhone);

      try {
        const response = await AuthService.kakaoLogin(
          String(userInfoRef.current?.data.id)
        );

        if (response.status < 300) {
          TokenService.setAccessToken(response.data.accessToken);
          StorageUtils.RecentLoginMethod.set("KAKAO");
          const myInfo = await fetchMyInfo().unwrap();

          if (myInfo.userStatus === "ACTIVE") {
            navigate(isModal ? "/search-results" : "/");
            return;
          }
          if (myInfo.userStatus === "PENDING") {
            navigate("/auth/additional-info", {
              state: {
                userInfo: {
                  firstName:
                    myInfo.name ||
                    userInfoRef.current?.data.kakao_account.name ||
                    "",
                  phoneNumber: myInfo.phone || normalizedPhone || "",
                  email:
                    myInfo.email ||
                    userInfoRef.current?.data.kakao_account.email ||
                    "",
                  providerId: myInfo.provider || "",
                },
                ssoName: "KAKAO",
              },
            });
            return;
          }
          navigate("/login");
        }
      } catch (error) {
        if (isAxiosError(error)) {
          const userInfo = { ...error.response?.data.userInfo };
          userInfo.email =
            userInfoRef.current?.data.kakao_account.email || userInfo.email || "";
          userInfo.firstName =
            userInfoRef.current?.data.kakao_account.name ||
            userInfo.firstName ||
            "";
          userInfo.phoneNumber = normalizedPhone || userInfo.phoneNumber || "";

          if (!userInfo.providerId) {
            navigate("/auth/additional-info", {
              state: { userInfo, ssoName: "KAKAO" },
            });
            return;
          }

          const form = new FormData();
          form.append("name", userInfo.firstName);
          form.append("email", userInfo.email);
          form.append("phone", userInfo.phoneNumber);
          form.append("ssoId", userInfo.providerId);
          form.append("ssoName", "KAKAO");

          try {
            const signup = await register(form).unwrap();
            TokenService.setAccessToken(signup.accessToken);
            StorageUtils.RecentLoginMethod.set("KAKAO");
            navigate("/auth/additional-info", {
              state: { userInfo, ssoName: "KAKAO" },
            });
            return;
          } catch {
            alert("KAKAO Social signup failed");
            navigate("/login");
            return;
          }
        }
        navigate("/login");
      }
    };
    getToken();
  }, [code, fetchMyInfo, register, isModal, navigate]);

  return <LoadingSpinner />;
};
export default KaKaoCallback;
